import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

const api =
  new API({
    tokenKey:
      "platformAccessToken",
  });

const initialState = {
  leads: [],
  selectedLead: null,
  summary: {
    total: 0,
    NEW: 0,
    UNDER_REVIEW: 0,
    CONTACTED: 0,
    OFFERED: 0,
    ACCEPTED: 0,
    REJECTED: 0,
    CLOSED: 0,
  },
  filters: {
    page: 1,
    limit: 20,
    search: "",
    status: "",
    deviceType: "",
    tenantId: "",
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filterOptions: {
    statuses: [],
    deviceTypes: [],
    tenants: [],
  },
  listLoading: false,
  detailsLoading: false,
  actionLoading: false,
  error: null,
};

const qs = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      sp.append(key, value);
    }
  });
  return sp.toString();
};

const err = (error) => ({
  message:
    error?.response?.data?.message ||
    error?.response?.message ||
    error?.message ||
    "Something went wrong.",
});

export const fetchPlatformSellDeviceLeads =
  createAsyncThunk(
    "platformSellDeviceLead/list",
    async (params = {}, { rejectWithValue }) => {
      try {
        const query = qs(params);
        return await api.get(
          query
            ? `${config.platformSellDeviceLeads}?${query}`
            : config.platformSellDeviceLeads,
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const fetchPlatformSellDeviceLeadSummary =
  createAsyncThunk(
    "platformSellDeviceLead/summary",
    async (_, { rejectWithValue }) => {
      try {
        return await api.get(
          config.platformSellDeviceLeadSummary,
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const fetchPlatformSellDeviceLeadFilters =
  createAsyncThunk(
    "platformSellDeviceLead/filters",
    async (_, { rejectWithValue }) => {
      try {
        return await api.get(
          config.platformSellDeviceLeadFilters,
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const fetchPlatformSellDeviceLeadById =
  createAsyncThunk(
    "platformSellDeviceLead/details",
    async (id, { rejectWithValue }) => {
      try {
        return await api.get(
          config.platformSellDeviceLeadById(id),
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const updatePlatformSellDeviceLeadStatus =
  createAsyncThunk(
    "platformSellDeviceLead/status",
    async ({ id, status }, { rejectWithValue }) => {
      try {
        return await api.patch(
          config.platformSellDeviceLeadStatus(id),
          { status },
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const updatePlatformSellDeviceLeadNotes =
  createAsyncThunk(
    "platformSellDeviceLead/notes",
    async ({ id, notes }, { rejectWithValue }) => {
      try {
        return await api.patch(
          config.platformSellDeviceLeadNotes(id),
          { notes },
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

export const updatePlatformSellDeviceLeadOffer =
  createAsyncThunk(
    "platformSellDeviceLead/offer",
    async (
      {
        id,
        offerPrice,
        offerNotes,
      },
      { rejectWithValue }
    ) => {
      try {
        return await api.patch(
          config.platformSellDeviceLeadOffer(id),
          {
            offerPrice,
            offerNotes,
          },
          true
        );
      } catch (error) {
        return rejectWithValue(err(error));
      }
    }
  );

const slice = createSlice({
  name: "platformSellDeviceLead",
  initialState,
  reducers: {
    setPlatformSellDeviceLeadFilters(state, action) {
      state.filters = {
        ...state.filters,
        ...(action.payload || {}),
      };
    },
    clearSelectedPlatformSellDeviceLead(state) {
      state.selectedLead = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlatformSellDeviceLeads.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchPlatformSellDeviceLeads.fulfilled, (state, action) => {
        state.listLoading = false;
        state.leads = Array.isArray(action.payload?.data)
          ? action.payload.data
          : [];
        state.pagination = {
          ...state.pagination,
          ...(action.payload?.pagination || {}),
        };
      })
      .addCase(fetchPlatformSellDeviceLeads.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload;
      });

    builder.addCase(
      fetchPlatformSellDeviceLeadSummary.fulfilled,
      (state, action) => {
        state.summary = {
          ...state.summary,
          ...(action.payload?.data || {}),
        };
      }
    );

    builder.addCase(
      fetchPlatformSellDeviceLeadFilters.fulfilled,
      (state, action) => {
        state.filterOptions = {
          statuses: Array.isArray(action.payload?.data?.statuses)
            ? action.payload.data.statuses
            : [],
          deviceTypes: Array.isArray(action.payload?.data?.deviceTypes)
            ? action.payload.data.deviceTypes
            : [],
          tenants: Array.isArray(action.payload?.data?.tenants)
            ? action.payload.data.tenants
            : [],
        };
      }
    );

    builder
      .addCase(fetchPlatformSellDeviceLeadById.pending, (state) => {
        state.detailsLoading = true;
      })
      .addCase(fetchPlatformSellDeviceLeadById.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedLead = action.payload?.data || null;
      });

    [
      updatePlatformSellDeviceLeadStatus,
      updatePlatformSellDeviceLeadNotes,
      updatePlatformSellDeviceLeadOffer,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.actionLoading = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.actionLoading = false;
          const updated = action.payload?.data;
          if (!updated) return;

          const id = String(updated._id || updated.id || "");
          state.leads = state.leads.map((lead) =>
            String(lead._id || lead.id || "") === id
              ? updated
              : lead
          );

          if (
            String(state.selectedLead?._id || state.selectedLead?.id || "") === id
          ) {
            state.selectedLead = updated;
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          state.actionLoading = false;
          state.error = action.payload;
        });
    });
  },
});

export const {
  setPlatformSellDeviceLeadFilters,
  clearSelectedPlatformSellDeviceLead,
} = slice.actions;

export const selectPlatformSellDeviceLeadState =
  (state) =>
    state.platformSellDeviceLead ||
    initialState;

export default slice.reducer;
